import React, { useEffect, useState } from "react";

import { AlertTriangle, LockOpen, Unlock } from "lucide-react";
import Swal from "sweetalert2";

import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { useClientCreditUnlock } from "~/hooks/useClientCreditUnlock";
import { UnlockType } from "~/types/clientCreditUnlock";

interface CreditUnlockAlertProps {
  clientId: string;
  unlockType: UnlockType;
  title: string;
  description: string;
  onUnlockChange?: (isUnlocked: boolean) => void;
}

export const CreditUnlockAlert: React.FC<CreditUnlockAlertProps> = ({
  clientId,
  unlockType,
  title,
  description,
  onUnlockChange,
}) => {
  const {
    activeUnlock,
    isUnlocked,
    canUnlock,
    isLoading,
    isUnlocking,
    unlock,
    revoke,
  } = useClientCreditUnlock(clientId, unlockType);

  const [hasNotified, setHasNotified] = useState(false);

  useEffect(() => {
    if (!isLoading && !hasNotified) {
      onUnlockChange?.(isUnlocked);
      setHasNotified(true);
    }
  }, [isLoading, isUnlocked, onUnlockChange, hasNotified]);

  useEffect(() => {
    if (hasNotified) {
      onUnlockChange?.(isUnlocked);
    }
  }, [isUnlocked, onUnlockChange, hasNotified]);

  const handleUnlock = async () => {
    const result = await Swal.fire({
      title: "Desbloquear cliente",
      input: "textarea",
      inputLabel: "Razón del desbloqueo (opcional)",
      inputPlaceholder: "Ingrese la razón...",
      showCancelButton: true,
      confirmButtonText: "Desbloquear",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#16a34a",
    });

    if (result.isConfirmed) {
      const success = await unlock(result.value || undefined);
      if (success) {
        Swal.fire({
          icon: "success",
          title: "Cliente desbloqueado",
          text: "El cliente puede crear notas de venta hasta que se revoque el desbloqueo.",
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudo desbloquear el cliente. Intente nuevamente.",
        });
      }
    }
  };

  const handleRevoke = async () => {
    const result = await Swal.fire({
      title: "Revocar desbloqueo",
      text: "El cliente volverá a estar bloqueado para crear notas de venta.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Revocar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc2626",
    });

    if (result.isConfirmed) {
      const success = await revoke();
      if (success) {
        Swal.fire({
          icon: "success",
          title: "Desbloqueo revocado",
          timer: 1500,
          showConfirmButton: false,
        });
      }
    }
  };

  if (isLoading) {
    return null;
  }

  if (isUnlocked && activeUnlock) {
    const unlockerName = activeUnlock.unlocker_profile
      ? `${activeUnlock.unlocker_profile.name || ""} ${activeUnlock.unlocker_profile.lastname || ""}`.trim()
      : "Usuario";

    return (
      <Alert className="mb-3 border-green-200 bg-green-50 px-2 py-2 text-green-800 sm:mb-6 sm:px-4 sm:py-4">
        <LockOpen className="h-4 w-4 text-green-600" />
        <AlertTitle className="text-sm sm:text-base">
          Cliente desbloqueado
        </AlertTitle>
        <AlertDescription className="text-xs sm:text-sm">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <span>
              Desbloqueado por {unlockerName}.
              {activeUnlock.reason && (
                <span className="ml-1 italic">
                  Razón: {activeUnlock.reason}
                </span>
              )}
            </span>
            {canUnlock && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRevoke}
                disabled={isUnlocking}
                className="mt-2 border-green-300 text-green-700 hover:bg-green-100 sm:mt-0"
              >
                Revocar
              </Button>
            )}
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert className="mb-3 border-red-200 bg-red-50 px-2 py-2 text-red-800 sm:mb-6 sm:px-4 sm:py-4">
      <AlertTriangle className="h-4 w-4 text-red-600" />
      <AlertTitle className="text-sm sm:text-base">{title}</AlertTitle>
      <AlertDescription className="text-xs sm:text-sm">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <span>{description}</span>
          {canUnlock && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleUnlock}
              disabled={isUnlocking}
              className="mt-2 border-red-300 text-red-700 hover:bg-red-100 sm:mt-0"
            >
              <Unlock className="mr-1 h-3 w-3" />
              Desbloquear
            </Button>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
};
